import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import "./css/Home.css";

const TESTIMONIALS = [
  {
    text: "They've been servicing our garden for a few years now and have transformed our outdoor space into something truly magical. Professional, reliable, and always exceeding expectations.",
    author: "Rich Mason",
    location: "Ada, MI",
  },
  {
    text: "Enchanted Gardener is... enchanting! They take wonderful ideas and turn them into reality. Our backyard is now our favorite room in the house.",
    author: "Suzy Cox",
    location: "Grand Rapids, MI",
  },
  {
    text: "The team's attention to detail is remarkable. They've maintained our property for over five years and it has never looked better.",
    author: "Michael Torres",
    location: "East Grand Rapids, MI",
  },
];

const SERVICES = [
  {
    title: "Garden Design",
    description: "Custom designs that transform ordinary spaces into extraordinary outdoor retreats.",
    image: "/assets/more-eg-photos/fresh-landscape-backyard2.jpg",
  },
  {
    title: "Maintenance",
    description: "Professional care to keep your garden thriving throughout every season.",
    image: "/assets/eg-images/maintenance1.JPG",
  },
  {
    title: "Seasonal Planters",
    description: "Beautiful container arrangements that bring color and life year-round.",
    image: "/assets/eg-images/After H1 back corner.Final.jpg",
  },
];

export default function Home({ onQuoteClick }) {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const [isVisible, setIsVisible] = useState({});
  const aboutRef = useRef(null);
  const servicesRef = useRef(null);
  const testimonialRef = useRef(null);

  // Intersection Observer for animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    const refs = [aboutRef, servicesRef, testimonialRef];
    refs.forEach((ref) => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % TESTIMONIALS.length);
    }, 6000);
    return () => clearInterval(interval);
  }, []);

  const handleServicesClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/services");
  };

  const handleAboutClick = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate("/about");
  };

  return (
    <div className="home">
      {/* ===== HERO SECTION ===== */}
      <section className="hero">
        <div className="hero-background">
          <img
            src="/assets/eg-images/yard-bench-eg-og.jpg"
            alt="Beautiful garden landscape"
            className="hero-image"
          />
          <div className="hero-overlay" />
        </div>

        <div className="hero-content">
          <span className="hero-tagline animate-fade-in-down">
            West Michigan's Premier Garden Service
          </span>
          <h1 className="hero-title animate-fade-in-up delay-1">
            Cultivating<br />
            <em>Extraordinary</em><br />
            Outdoor Spaces
          </h1>
          <p className="hero-subtitle animate-fade-in-up delay-2">
            Professional garden design, maintenance, and seasonal care
            since 2007
          </p>
          <div className="hero-actions animate-fade-in-up delay-3">
            <button
              className="btn-primary-custom"
              onClick={onQuoteClick}
            >
              Get a Quote
            </button>
            <button className="btn-secondary-custom" onClick={handleServicesClick}>
              Our Services
            </button>
          </div>
        </div>
      </section>

      {/* ===== ABOUT SECTION ===== */}
      <section
        id="about-section"
        ref={aboutRef}
        className={`about-section ${isVisible["about-section"] ? "visible" : ""}`}
      >
        <div className="about-container">
          <div className="about-content">
            <span className="section-label">About Us</span>
            <h2 className="about-title">
              Transforming Landscapes,<br />
              Creating Legacies
            </h2>
            <p className="about-text">
              At Enchanted Gardener, we believe every outdoor space holds the
              potential for extraordinary beauty. For over 17 years, we've
              partnered with West Michigan homeowners to design, nurture, and
              transform their gardens into living works of art.
            </p>
            <p className="about-text">
              Our approach combines horticultural expertise with an artistic eye,
              ensuring your landscape not only thrives but tells a unique story.
            </p>
            <button className="btn-link" onClick={handleAboutClick}>
              Learn Our Story <span className="arrow">&rarr;</span>
            </button>
          </div>

          <div className="about-images">
            <div className="about-image-wrapper main">
              <img
                src="/assets/more-eg-photos/after-backyard.jpg"
                alt="Transformed backyard garden"
              />
            </div>
            <div className="about-image-wrapper secondary">
              <img
                src="/assets/more-eg-photos/stone-path.jpg"
                alt="Garden stone pathway"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES SECTION ===== */}
      <section
        id="services-section"
        ref={servicesRef}
        className={`services-section ${isVisible["services-section"] ? "visible" : ""}`}
      >
        <div className="services-container">
          <div className="services-header">
            <span className="section-label">What We Do</span>
            <h2 className="services-title">Expert Garden Services</h2>
            <p className="services-subtitle">
              Comprehensive solutions for every aspect of your outdoor space
            </p>
          </div>

          <div className="services-grid">
            {SERVICES.map((service, index) => (
              <article
                key={service.title}
                className="service-card"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="service-image-wrapper">
                  <img src={service.image} alt={service.title} />
                  <div className="service-image-overlay" />
                </div>
                <div className="service-content">
                  <h3 className="service-title">{service.title}</h3>
                  <p className="service-description">{service.description}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="services-cta">
            <button className="btn-primary-custom" onClick={handleServicesClick}>
              View All Services
            </button>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS SECTION ===== */}
      <section
        id="testimonial-section"
        ref={testimonialRef}
        className={`testimonial-section ${isVisible["testimonial-section"] ? "visible" : ""}`}
      >
        <div className="testimonial-container">
          <span className="section-label">Testimonials</span>
          <h2 className="testimonial-title">What Our Clients Say</h2>

          <div className="testimonial-carousel">
            {TESTIMONIALS.map((testimonial, index) => (
              <blockquote
                key={index}
                className={`testimonial-item ${index === activeTestimonial ? "active" : ""}`}
              >
                <p className="testimonial-text">"{testimonial.text}"</p>
                <footer className="testimonial-author">
                  <span className="author-name">{testimonial.author}</span>
                  <span className="author-location">{testimonial.location}</span>
                </footer>
              </blockquote>
            ))}
          </div>

          <div className="testimonial-indicators">
            {TESTIMONIALS.map((_, index) => (
              <button
                key={index}
                className={`indicator ${index === activeTestimonial ? "active" : ""}`}
                onClick={() => setActiveTestimonial(index)}
                aria-label={`View testimonial ${index + 1}`}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ===== CTA SECTION ===== */}
      <section className="cta-section">
        <div className="cta-background">
          <img
            src="/assets/eg-images/bloom-flowers-landscape.jpg"
            alt="Blooming garden flowers"
          />
          <div className="cta-overlay" />
        </div>
        <div className="cta-content">
          <h2 className="cta-title">Ready to Transform Your Space?</h2>
          <p className="cta-subtitle">
            Let's create something beautiful together. Get a free consultation
            and discover what's possible for your outdoor space.
          </p>
          <button
            className="btn-primary-custom cta-button"
            onClick={onQuoteClick}
          >
            Start Your Project
          </button>
        </div>
      </section>

      {/* ===== CONTACT STRIP ===== */}
      <section className="contact-strip">
        <div className="contact-strip-content">
          <div className="contact-item">
            <span className="contact-label">Call Us</span>
            <a href="tel:6166820808" className="contact-value">(616) 682-0808</a>
          </div>
          <div className="contact-divider" />
          <div className="contact-item">
            <span className="contact-label">Email</span>
            <a href="mailto:info@enchanted-gardener.com" className="contact-value">
              info@enchanted-gardener.com
            </a>
          </div>
          <div className="contact-divider" />
          <div className="contact-item">
            <span className="contact-label">Location</span>
            <span className="contact-value">Ada, Michigan</span>
          </div>
        </div>
      </section>
    </div>
  );
}
